import { Button, PopoverMenu } from '@wix/design-system';
import { useTranslation } from '@wix/fe-essentials-standalone';
import { ReactNode, useState, useMemo, useCallback } from 'react';
import {
  PROFILE_POPOVER_MAX_WIDTH,
  PROFILE_POPOVER_MIN_WIDTH,
} from '@utils/constants';
import s from './MoreItemsButton.module.scss';

interface MoreItemsButtonProps<T> {
  items: T[];
  maxVisibleItems: number;
  isMobileView?: boolean;
  renderItem: (item: T, index: number) => ReactNode;
  className?: string;
  onShowMore?: () => void;
  onShowLess?: () => void;
}

const MoreItemsButton = <T,>({
  items,
  maxVisibleItems,
  isMobileView,
  renderItem,
  className,
  onShowMore,
  onShowLess,
}: MoreItemsButtonProps<T>): JSX.Element => {
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);

  const hasMoreItems = items.length > maxVisibleItems;
  const visibleItems =
    showAll && isMobileView ? items : items.slice(0, maxVisibleItems);
  const remainingItems = !isMobileView ? items.slice(maxVisibleItems) : [];

  const handleMobileShowMore = useCallback(() => {
    onShowMore?.();
    setShowAll(true);
  }, [onShowMore]);

  const handleMobileShowLess = useCallback(() => {
    onShowLess?.();
    setShowAll(false);
  }, [onShowLess]);

  const moreCount = items.length - maxVisibleItems;

  const showMoreButton = useMemo(
    () => (
      <Button
        className={s.showButton}
        dataHook="tag-more-count"
        size={isMobileView ? 'small' : 'tiny'}
        priority="secondary"
        skin="dark"
        onClick={isMobileView ? handleMobileShowMore : undefined}
      >
        {t('profile-page-services-more-count-label', {
          moreCount,
        })}
      </Button>
    ),
    [isMobileView, moreCount, t, handleMobileShowMore],
  );

  const showLessButton = useMemo(
    () => (
      <Button
        className={s.showButton}
        dataHook="tag-show-less"
        size={isMobileView ? 'small' : 'tiny'}
        priority="secondary"
        skin="dark"
        onClick={handleMobileShowLess}
      >
        {t('profile-page-services-show-less-label')}
      </Button>
    ),
    [isMobileView, t, handleMobileShowLess],
  );

  return (
    <div
      className={`${s.root} ${className || ''}`}
      data-hook="more-items-button-root"
    >
      {visibleItems.map((item, index) => renderItem(item, index))}
      {hasMoreItems &&
        (isMobileView ? (
          showAll ? (
            showLessButton
          ) : (
            showMoreButton
          )
        ) : (
          <PopoverMenu
            className={s.popoverMenu}
            appendTo="window"
            placement="left"
            minWidth={PROFILE_POPOVER_MIN_WIDTH}
            maxWidth={PROFILE_POPOVER_MAX_WIDTH}
            triggerElement={showMoreButton}
            onShow={onShowMore}
            onHide={onShowLess}
            dataHook="more-items-popover"
          >
            <div className={s.popoverContent} role="presentation">
              {remainingItems.map((item, index) =>
                renderItem(item, index + maxVisibleItems),
              )}
            </div>
          </PopoverMenu>
        ))}
    </div>
  );
};

export default MoreItemsButton;
